import { useEffect, useState, useCallback, useRef } from 'react';
import * as Y from 'yjs';
import * as awarenessProtocol from 'y-protocols/awareness';
import { ElectricYjsProvider, type SyncMode } from '../lib/electric/ElectricYjsProvider';
import { YjsTabSync } from '../lib/electric/YjsTabSync';
import { base64ToUint8Array, uint8ArrayToBase64 } from '../utils/yjs';
import { useAuthenticatedFetch } from './useAuth';
import { Logger } from '@/utils/logger';
import { LocalDocBackupStore } from '@/lib/persistence/LocalDocBackupStore';
import { ShapeMapAdapter } from '../lib/diagram/ShapeMapAdapter';
import { ShapePersistenceGateway } from '../lib/diagram/shapeGeometry';

const logger = Logger.getLoggerWithPrefix('useDiagramWithElectricProvider');
const SNAPSHOT_LOAD_ATTEMPTS = 3;
const SNAPSHOT_RETRY_DELAYS_MS = [250, 1_000] as const;
// Awareness service URL (external WebSocket service, not through worker)
const AWARENESS_SERVICE_URL = import.meta.env.VITE_AWARENESS_SERVICE_URL || 'ws://localhost:8080';
logger.info('Awareness service URL:', AWARENESS_SERVICE_URL);

import { getStableTabClientId } from '../utils/stableClientId';
import { getCookie } from '../lib/cookies';
import { useAuthStore } from '../stores/authStore';

// Per-diagram instances to prevent StrictMode duplication
// StrictMode in dev causes double-mounting which would create multiple providers
const diagramInstances = new Map<string, { doc: Y.Doc; awareness: awarenessProtocol.Awareness }>();

interface UseDiagramWithElectricOptions {
  diagramId: string;
  userId: string;
  userName?: string;
  isLinkMode?: boolean;
  enabled?: boolean;
  deferElectric?: boolean;
}

export function useDiagramWithElectricProvider(options: UseDiagramWithElectricOptions & { canWrite?: boolean }) {
  const {
    diagramId,
    userId,
    userName,
    isLinkMode = false,
    canWrite = true,
    enabled = true,
    deferElectric = false,
  } = options;
  const authenticatedFetch = useAuthenticatedFetch();
  const authStoreToken = useAuthStore((s) => s.token);

  // Create stable Yjs document + awareness (per-diagram, StrictMode safe)
  // Use stable tab-level clientId that persists across refreshes
  const [ydoc] = useState(() => {
    const existing = diagramInstances.get(diagramId);
    if (existing) return existing.doc;
    const doc = new Y.Doc();
    doc.clientID = getStableTabClientId(diagramId);
    const aware = new awarenessProtocol.Awareness(doc);
    aware.clientID = doc.clientID;
    diagramInstances.set(diagramId, { doc, awareness: aware });
    return doc;
  });

  const [awareness] = useState(() => {
    const existing = diagramInstances.get(diagramId);
    if (existing) return existing.awareness;
    // Should not reach here since ydoc init creates both, but handle defensively
    const aware = new awarenessProtocol.Awareness(ydoc);
    aware.clientID = ydoc.clientID;
    diagramInstances.set(diagramId, { doc: ydoc, awareness: aware });
    return aware;
  });

  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [provider, setProvider] = useState<ElectricYjsProvider | null>(null);
  const [connectionState, setConnectionState] = useState<'connected' | 'disconnected'>('disconnected');
  const [electricSynced, setElectricSynced] = useState(false);  // Track Electric sync state
  const [syncMode, setSyncMode] = useState<SyncMode>(deferElectric ? 'solo' : 'collab');
  const [pendingCount, setPendingCount] = useState(0);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const providerInitialized = useRef(false);
  const providerCanWrite = useRef<boolean | null>(null);
  const lastDiagramId = useRef<string | null>(null);
  const snapshotLoaded = useRef(false);  // Track if snapshot was loaded for current diagram
  const documentEpochRef = useRef(0);
  const lastPushedSeqRef = useRef<number | null>(null);
  const pushInFlightRef = useRef(false);
  const lastPushTimestampRef = useRef<number>(0); // M3: debounce snapshot push
  const [pushGeneration, setPushGeneration] = useState(0);
  const tabSyncRef = useRef<YjsTabSync | null>(null);
  const readyOnceRef = useRef(false); // M1: once ready, never flip back to false
  const catchUpSeqRef = useRef<number | null>(null); // M5: max seq from catch-up for latestConfirmedSeq init

  // Reset state when diagram changes
  useEffect(() => {
    if (!enabled) return;
    if (lastDiagramId.current !== null && lastDiagramId.current !== diagramId) {
      logger.info('Diagram changed, resetting state');
      
      // Reset flags
      providerInitialized.current = false;
      providerCanWrite.current = null;
      snapshotLoaded.current = false;
      setIsReady(false);
      setElectricSynced(false);
      setError(null);
      lastPushedSeqRef.current = null;
      pushInFlightRef.current = false;
      readyOnceRef.current = false;
      catchUpSeqRef.current = null;
      setSyncMode(deferElectric ? 'solo' : 'collab');
      setPendingCount(0);
      setPushGeneration(0);
      setSaveStatus('idle');

      // Destroy old tab sync to prevent resource leak (M4)
      if (tabSyncRef.current) {
        tabSyncRef.current.destroy();
        tabSyncRef.current = null;
      }

      // Clear the existing ydoc content (but keep the same doc instance)
      ydoc.transact(() => {
        new ShapePersistenceGateway(new ShapeMapAdapter(ydoc.getMap('shapes'))).clear();
        ydoc.getMap('connectors').clear();
      }, 'reset');

      // Remove old diagram entry from map
      diagramInstances.delete(lastDiagramId.current);

      lastDiagramId.current = diagramId;
    } else if (lastDiagramId.current === null) {
      lastDiagramId.current = diagramId;
    }
  }, [enabled, diagramId, ydoc, deferElectric]);

  // Safety net: unblock UI if Electric's "up-to-date" signal hasn't arrived within 8s
  // Only applies in collab mode — solo mode doesn't wait for Electric
  useEffect(() => {
    if (!enabled) return;
    if (syncMode === 'solo') return;
    if (electricSynced || !isReady) return;
    const timeout = setTimeout(() => {
      logger.warn('Electric sync timeout (8s), unblocking UI');
      setElectricSynced(true);
    }, 8000);
    return () => clearTimeout(timeout);
  }, [enabled, electricSynced, isReady, syncMode]);

  // Auto-reset saveStatus to idle 3s after becoming 'saved'
  useEffect(() => {
    if (saveStatus !== 'saved') return;
    const timer = setTimeout(() => setSaveStatus('idle'), 3000);
    return () => clearTimeout(timer);
  }, [saveStatus]);

  // Set initial awareness state
  useEffect(() => {
    if (!enabled) return;
    const displayName = userName && userName !== 'Anonymous User'
      ? userName
      : `User ${ydoc.clientID}`;

    awareness.setLocalStateField('user', {
      id: userId,
      name: displayName,
      color: `hsl(${(ydoc.clientID * 137.5) % 360}, 70%, 50%)`,
      timestamp: Date.now(),
    });
  }, [enabled, awareness, userId, userName, ydoc.clientID]);

  // Load initial snapshot before connecting to Electric (ONCE per diagram)
  useEffect(() => {
    if (!enabled) return;
    // Skip if already loaded for this diagram
    if (snapshotLoaded.current) {
      return;
    }

    let cancelled = false;

    async function loadSnapshot(retryCount = 0) {
      // Hydrate each attempt in isolation. Applying a partial/stale attempt to
      // the live Y.Doc would make a later retry additive and could resurrect
      // content that the authoritative snapshot deleted.
      const hydratedDoc = new Y.Doc();
      try {
        const snapshotUrl = isLinkMode
          ? `/api/link/diagrams/${diagramId}/snapshot`
          : `/api/diagrams/${diagramId}/snapshot`;

        const response = isLinkMode
          ? await fetch(snapshotUrl, { credentials: 'include' })
          : await authenticatedFetch(snapshotUrl);

        if (!response.ok) {
          throw new Error(`Failed to load snapshot: ${response.statusText}`);
        }

        const data = await response.json();
        const nextDocumentEpoch = Number.isFinite(Number(data.documentEpoch))
          ? Math.max(0, Number(data.documentEpoch))
          : 0;
        if (nextDocumentEpoch > documentEpochRef.current) {
          const { OutboxStorage } = await import('../lib/persistence/OutboxStorage');
          await OutboxStorage.deleteForDiagram(diagramId);
        }
        documentEpochRef.current = nextDocumentEpoch;
        logger.info('📥 Snapshot response', {
          hasDownloadUrl: !!data.downloadUrl,
          hasSnapshotBase64: !!data.snapshotBytesBase64,
          snapshotSeq: data.snapshotSeq,
          snapshotBase64Length: data.snapshotBytesBase64?.length ?? 0,
        });

        if (cancelled) return;

        // R2 path: download binary directly from R2 pre-signed URL
        if (data.downloadUrl) {
          const r2Response = await fetch(data.downloadUrl);
          if (!r2Response.ok) {
            throw new Error(`Failed to download snapshot from R2: ${r2Response.status}`);
          }
          const buffer = await r2Response.arrayBuffer();
          Y.applyUpdate(hydratedDoc, new Uint8Array(buffer), 'server');
          logger.info('📥 Applied R2 snapshot', { bufferSize: buffer.byteLength });
        } else if (data.snapshotBytesBase64) {
          // Legacy path: base64 inline
          const snapshotBytes = base64ToUint8Array(data.snapshotBytesBase64);
          Y.applyUpdate(hydratedDoc, snapshotBytes, 'server');
          logger.info('📥 Applied base64 snapshot', { snapshotSize: snapshotBytes.byteLength });
        } else if ((data.snapshotSeq ?? 0) > 0) {
          throw new Error('The saved diagram snapshot payload is unavailable');
        } else {
          logger.info('No existing snapshot; starting a new empty diagram');
        }

        if (cancelled) return;

        try {
          const shapes = hydratedDoc.getMap('shapes');
          const connectors = hydratedDoc.getMap('connectors');
          logger.debug('Debug SDI: Snapshot loaded', {
            diagramId,
            shapes: shapes?.size,
            connectors: connectors?.size,
          });
        } catch {}

        // Catch up on updates that arrived after the snapshot
        const snapshotSeq = data.snapshotSeq ?? 0;
        const catchUpUrl = isLinkMode
          ? `/api/link/diagrams/${diagramId}/updates?since=${snapshotSeq}`
          : `/api/diagrams/${diagramId}/updates?since=${snapshotSeq}`;
        const catchUpRes = isLinkMode
          ? await fetch(catchUpUrl, { credentials: 'include' })
          : await authenticatedFetch(catchUpUrl);

        if (catchUpRes.ok && !cancelled) {
          const catchUp = await catchUpRes.json();
          logger.info('📥 Catch-up response', {
            updatesCount: catchUp.updates?.length ?? 0,
            gapDetected: catchUp.gapDetected,
            currentSnapshotSeq: catchUp.currentSnapshotSeq,
            truncated: catchUp.truncated,
            sinceSeq: snapshotSeq,
          });
          if (catchUp.gapDetected) {
            if (retryCount < SNAPSHOT_LOAD_ATTEMPTS - 1) {
              logger.warn('Gap detected in catch-up, re-fetching snapshot');
              return loadSnapshot(retryCount + 1);
            }
            throw new Error('The complete diagram update history could not be loaded');
          }
          let maxCatchUpSeq = snapshotSeq;
          for (const u of catchUp.updates || []) {
            Y.applyUpdate(hydratedDoc, base64ToUint8Array(u.data), 'server');
            const uSeq = typeof u.seq === 'number' ? u.seq : (typeof u.seq === 'string' ? parseInt(u.seq, 10) : 0);
            if (uSeq > maxCatchUpSeq) maxCatchUpSeq = uSeq;
          }
          // M5: Store max seq so provider can be initialized with it
          catchUpSeqRef.current = maxCatchUpSeq > 0 ? maxCatchUpSeq : null;
          logger.info('📥 Catch-up complete', { maxCatchUpSeq, appliedUpdates: catchUp.updates?.length ?? 0 });
        } else if (!cancelled) {
          throw new Error(`Diagram catch-up failed with status ${catchUpRes.status}`);
        }

        if (cancelled) return;

        // Log final Y.Doc state after snapshot + catch-up
        try {
          const shapes = hydratedDoc.getMap('shapes');
          const connectors = hydratedDoc.getMap('connectors');
          logger.info('📊 Y.Doc state after load', {
            shapesCount: shapes?.size ?? 0,
            connectorsCount: connectors?.size ?? 0,
            shapeIds: Array.from(shapes?.keys() ?? []).slice(0, 10),
            connectorIds: Array.from(connectors?.keys() ?? []).slice(0, 10),
          });
        } catch {}

        Y.applyUpdate(ydoc, Y.encodeStateAsUpdate(hydratedDoc), 'server');
        snapshotLoaded.current = true;
        setError(null);
        setIsReady(true);
      } catch (err) {
        logger.error('Failed to load snapshot:', err);
        try { logger.debug('Debug SDI: Snapshot load failed', { diagramId, error: (err as Error)?.message }); } catch {}
        if (cancelled) return;
        const nextAttempt = retryCount + 1;
        if (nextAttempt < SNAPSHOT_LOAD_ATTEMPTS) {
          const retryDelay = SNAPSHOT_RETRY_DELAYS_MS[retryCount]
            ?? SNAPSHOT_RETRY_DELAYS_MS[SNAPSHOT_RETRY_DELAYS_MS.length - 1];
          logger.warn('Retrying diagram snapshot load', {
            diagramId,
            attempt: nextAttempt + 1,
            retryDelay,
          });
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          if (!cancelled) return loadSnapshot(nextAttempt);
          return;
        }
        // A failed snapshot is unknown state, never an empty diagram. Keeping
        // readiness false prevents Electric and snapshot persistence from
        // promoting a blank fallback over the last durable document.
        setError(err instanceof Error ? err : new Error('Failed to load diagram snapshot'));
      } finally {
        hydratedDoc.destroy();
      }
    }

    loadSnapshot();

    return () => {
      cancelled = true;
    };
  }, [enabled, diagramId, ydoc, authenticatedFetch, isLinkMode]);

  // Recover emergency backup from localStorage (if browser crashed or tab was killed)
  // Skip if this reload was triggered by a version restore (flag set in DiagramHistoryPanel)
  useEffect(() => {
    if (!enabled || !snapshotLoaded.current) return;
    const restoreFlag = `creately-restoring:${diagramId}`;
    if (sessionStorage.getItem(restoreFlag)) {
      sessionStorage.removeItem(restoreFlag);
      // Discard the emergency backup — it contains pre-restore state
      LocalDocBackupStore.recoverEmergency(diagramId);
      logger.info('Skipped emergency recovery (version restore in progress)');
      return;
    }
    const emergency = LocalDocBackupStore.recoverEmergency(diagramId);
    if (emergency) {
      logger.info('Applying emergency backup from localStorage');
      Y.applyUpdate(ydoc, emergency, 'emergency-recovery');
    }
  }, [enabled, diagramId, ydoc, isReady]);

  // Periodic backup: every 2 min if doc has changed
  useEffect(() => {
    if (!enabled || !isReady) return;
    const dirtyRef = { current: false };
    const handler = () => { dirtyRef.current = true; };
    ydoc.on('update', handler);

    const interval = setInterval(() => {
      if (dirtyRef.current) {
        dirtyRef.current = false;
        LocalDocBackupStore.save(diagramId, ydoc, 'periodic', null).catch(() => {});
      }
    }, 120_000);

    return () => {
      ydoc.off('update', handler);
      clearInterval(interval);
    };
  }, [enabled, isReady, diagramId, ydoc]);

  // Connect to Electric after snapshot is loaded
  useEffect(() => {
    if (!enabled) return;
    logger.info('useEffect triggered', {
      isReady,
      providerInitialized: providerInitialized.current,
      lastDiagramId: lastDiagramId.current,
      currentDiagramId: diagramId,
      canWrite,
      isLinkMode
    });
    
    if (!isReady) return;
    
    // Prevent re-creating provider if already initialized
    // This prevents WebSocket connection thrashing from unnecessary re-renders
    if (providerInitialized.current && lastDiagramId.current === diagramId) {
      if (providerCanWrite.current !== canWrite) {
        logger.info('Provider write mode changed, recreating provider', {
          diagramId,
          previousCanWrite: providerCanWrite.current,
          canWrite,
        });
        if (provider) {
          provider.destroy();
          setProvider(null);
        }
        providerInitialized.current = false;
        setElectricSynced(false);
      } else {
        logger.info('Provider already initialized for diagram:', diagramId);
        return;
      }
    }

    // Clean up existing provider if diagram changed
    if (provider && lastDiagramId.current !== diagramId) {
      logger.info('Cleaning up existing provider for diagram change');
      provider.destroy();
      setProvider(null);
      providerInitialized.current = false;
      providerCanWrite.current = null;
    }

    const token = getCookie('gravity_token') ?? authStoreToken ?? undefined;

    // Build FULL Electric shape stream URL (with protocol and host)
    const isDev = window.location.port === '5174';
    const httpProtocol = window.location.protocol; // http: or https:
    const httpHost = isDev ? 'localhost:5174' : window.location.host;

    const electricShapePath = isLinkMode
      ? `/api/electric/link/diagrams/${diagramId}/updates`
      : `/api/electric/diagrams/${diagramId}/updates`;

    const electricShapeUrl = `${httpProtocol}//${httpHost}${electricShapePath}`;

    // Build write URL (absolute path)
    const writeUrl = isLinkMode
      ? `/api/link/diagrams/${diagramId}/updates`
      : `/api/diagrams/${diagramId}/updates`;

    // Awareness service URL - client connects directly (not through worker)
    // Format: ws://localhost:8080/diagrams/:id?token=...
    const awarenessServiceUrl = `${AWARENESS_SERVICE_URL}/diagrams/${diagramId}${token ? `?token=${token}` : ''}`;

    // Electric deferral is an explicit opt-in. The production document context
    // must connect eagerly so a writable editor can discover late joiners.
    logger.info('Creating provider', { deferElectric, canWrite, syncMode: deferElectric ? 'solo' : 'collab' });

    const electricProvider = new ElectricYjsProvider({
      diagramId,
      doc: ydoc,
      awareness,
      documentUpdates: {
        shapeUrl: electricShapeUrl, // FULL URL for Electric shape stream
        sendUrl: writeUrl, // Write endpoint (absolute path)
      },
      awarenessUpdates: {
        awarenessServiceUrl, // WebSocket URL for awareness service
      },
      authToken: token,
      documentEpoch: documentEpochRef.current,
      canWrite, // Pass write permission down
      deferElectric, // Skip Electric SSE for solo mode
      streamFloorSeq: catchUpSeqRef.current,
      onConnectionStateChange: (state) => {
        setConnectionState(state);
      },
      onSyncStateChange: (synced) => {
        logger.info('Electric synced:', synced);
        setElectricSynced(synced);
        try {
          const shapes = ydoc.getMap('shapes');
          const connectors = ydoc.getMap('connectors');
          logger.debug('Debug SDI: Electric sync state', {
            diagramId,
            synced,
            shapes: shapes?.size,
            connectors: connectors?.size
          });
        } catch {}
      },
      onPendingCountChange: (count) => setPendingCount(count),
      onSoloConfirm: (updateBase64, seq) => {
        logger.info('📡 onSoloConfirm callback', { seq, hasTabSync: !!tabSyncRef.current, updateSize: updateBase64.length });
        tabSyncRef.current?.broadcastUpdate(updateBase64, seq);
      },
      onSendError: () => setSaveStatus('error'),
      onBackupNeeded: (trigger) => {
        if (trigger === 'before_unload') {
          LocalDocBackupStore.saveEmergency(diagramId, ydoc);
        } else {
          LocalDocBackupStore.save(diagramId, ydoc, trigger, null).catch(() => {});
        }
      },
    });

    // Create YjsTabSync for cross-tab sync (solo mode uses this instead of Electric)
    if (tabSyncRef.current) tabSyncRef.current.destroy();
    tabSyncRef.current = new YjsTabSync(diagramId, ydoc, {
      // M5: Propagate cross-tab confirmed seqs so snapshot push uses correct seq
      onRemoteSeq: (seq) => electricProvider.updateConfirmedSeq(seq),
    });

    // Reset sync mode for the new provider.
    setSyncMode(deferElectric ? 'solo' : 'collab');
    if (!deferElectric) {
      // Eager clients enter collab mode while Electric catches up.
      setElectricSynced(false);
    }

    // M5: Initialize latestConfirmedSeq from catch-up data so snapshot push can fire on reload
    if (catchUpSeqRef.current !== null) {
      electricProvider.updateConfirmedSeq(catchUpSeqRef.current);
    }

    setProvider(electricProvider);
    providerInitialized.current = true;
    providerCanWrite.current = canWrite;

    return () => {
      electricProvider.destroy();
      if (tabSyncRef.current) {
        tabSyncRef.current.destroy();
        tabSyncRef.current = null;
      }
      providerInitialized.current = false;
      providerCanWrite.current = null;
      // M8: Clean diagramInstances to prevent memory leak across navigations
      diagramInstances.delete(diagramId);
    };
  }, [enabled, isReady, diagramId, isLinkMode, canWrite, authStoreToken, deferElectric]);

  // Cleanup awareness on unmount
  useEffect(() => {
    if (!enabled) return;
    const handleBeforeUnload = () => {
      if (awareness.getLocalState()) {
        awareness.setLocalState(null);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, awareness]);

  // Expose collaboration primitives for production-safe E2E verification.
  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    (window as any).__awareness = awareness;
    (window as any).__electricYjsProvider = provider;

    return () => {
      if ((window as any).__awareness === awareness) {
        delete (window as any).__awareness;
      }
      if ((window as any).__electricYjsProvider === provider) {
        delete (window as any).__electricYjsProvider;
      }
    };
  }, [enabled, awareness, provider]);

  // Awareness-triggered lazy Electric: detect remote collaborators → transition solo → collab
  // TODO(Phase 3): When the Processing Service ships, it joins awareness as a headless client
  // with `{ type: 'server_mutation', snapshotSeq }` state. This must be handled differently:
  // the client should re-fetch the snapshot from R2 instead of connecting Electric SSE,
  // because the Processing Service writes directly to R2 and bypasses diagram_updates.
  // See plans/archive/holistic-architecture-redesign-archived.md §6.8 for details.
  useEffect(() => {
    if (!enabled || !provider || syncMode === 'collab') return;

    const checkForCollaborators = () => {
      const states = awareness.getStates();
      let hasRemote = false;
      const now = Date.now();
      states.forEach((state, clientId) => {
        if (clientId === awareness.clientID) return;
        const user = state.user as Record<string, unknown> | undefined;
        if (!user?.id || user.id === userId) return;
        // C1: Only treat awareness states with a recent timestamp as active collaborators.
        // Stale states from crashed clients can linger in the awareness service for up to 30s
        // (y-protocols outdatedTimeout). Require a timestamp within the last 60s to be safe.
        const ts = typeof user.timestamp === 'number' ? (user.timestamp as number) : 0;
        if (now - ts > 60_000) return;
        hasRemote = true;
      });
      if (hasRemote) {
        logger.info('Remote collaborator detected, transitioning to collab mode');
        // L4: Switch provider to collab FIRST so sendUpdate uses collab drain path
        // M2: setSyncMode flushes batch buffer before switching
        provider.setSyncMode('collab');
        provider.connectElectricNow();
        // React state update last (async batched)
        setSyncMode('collab');
      }
    };

    awareness.on('change', checkForCollaborators);
    checkForCollaborators();
    return () => awareness.off('change', checkForCollaborators);
  }, [enabled, awareness, provider, syncMode, userId]);

  // Encode snapshot as raw binary for R2 upload
  const encodeSnapshotBinary = useCallback(() => {
    return Y.encodeStateAsUpdate(ydoc);
  }, [ydoc]);

  // Legacy encode for backward compatibility
  const encodeSnapshot = useCallback(() => {
    const snapshot = Y.encodeStateAsUpdate(ydoc);
    const stateVector = Y.encodeStateVector(ydoc);
    return {
      snapshotBytesBase64: uint8ArrayToBase64(snapshot),
      stateVectorBase64: uint8ArrayToBase64(stateVector),
    };
  }, [ydoc]);

  const flushPendingUpdates = useCallback(async () => {
    if (provider) await provider.flush();
  }, [provider]);

  // M1: Once ready, never flip back to false (prevents UI flicker during solo→collab transition)
  const currentIsReady = enabled ? (isReady && (syncMode === 'solo' || electricSynced)) : true;
  if (currentIsReady) readyOnceRef.current = true;

  const returnValue = {
    ydoc,
    awareness,
    provider,
    isConnected: enabled ? (isReady && !!provider) : false,
    isReady: readyOnceRef.current,
    error,
    encodeSnapshot,
    flushPendingUpdates,
    connectionState, // Expose connection state for lazy WebSocket
    pendingCount,
    isSyncing: pendingCount > 0,
    saveStatus,
    syncMode, // C1: Expose for broadcaster gating
  };

  useEffect(() => {
    if (!enabled) return;
    if (!provider || !isReady || !canWrite) {
      return;
    }
    // In collab mode, wait for Electric sync before pushing; in solo mode, proceed immediately
    if (syncMode === 'collab' && !electricSynced) {
      return;
    }
    // C1: In solo mode, wait for all in-flight writes to confirm before pushing.
    // This prevents pushing a snapshot that includes local edits whose POST might fail.
    // M3: Failsafe — if latestConfirmedSeq has advanced significantly beyond last push,
    // force a push even if pendingCount > 0 to prevent unbounded diagram_updates growth.
    const SEQ_DELTA_FAILSAFE = 500;
    const latestConfirmed = syncMode === 'solo' ? provider.latestConfirmedSeq : null;
    const seqDelta = (latestConfirmed !== null && lastPushedSeqRef.current !== null)
      ? latestConfirmed - lastPushedSeqRef.current
      : 0;
    if (syncMode === 'solo' && pendingCount > 0 && seqDelta < SEQ_DELTA_FAILSAFE) {
      return;
    }
    // Use latestConfirmedSeq in solo mode, latestSeq (from Electric) in collab
    const latestSeq = syncMode === 'solo'
      ? provider.latestConfirmedSeq
      : provider.getLatestSeq();
    if (latestSeq === null) {
      return;
    }
    if (pushInFlightRef.current) {
      return;
    }
    if (lastPushedSeqRef.current !== null && latestSeq <= lastPushedSeqRef.current) {
      return;
    }

    // M3: Minimum 30s interval between snapshot pushes to prevent rapid fire
    const MIN_PUSH_INTERVAL_MS = 30_000;
    const elapsed = Date.now() - lastPushTimestampRef.current;
    if (lastPushTimestampRef.current > 0 && elapsed < MIN_PUSH_INTERVAL_MS) {
      const delay = MIN_PUSH_INTERVAL_MS - elapsed;
      logger.info('Snapshot push debounced, retrying in', { delay, latestSeq });
      const timer = setTimeout(() => setPushGeneration(g => g + 1), delay);
      return () => clearTimeout(timer);
    }

    let cancelled = false;
    const pushSnapshot = async () => {
      pushInFlightRef.current = true;
      setSaveStatus('saving');
      try {
        // Log Y.Doc state at snapshot push time
        try {
          const shapes = ydoc.getMap('shapes');
          const connectors = ydoc.getMap('connectors');
          logger.info('📤 Snapshot push starting', {
            latestSeq,
            shapesCount: shapes?.size ?? 0,
            connectorsCount: connectors?.size ?? 0,
          });
        } catch {}
        // Send binary snapshot directly (Content-Type: application/octet-stream)
        // Snapshot seq passed via X-Snapshot-Seq header
        const binarySnapshot = encodeSnapshotBinary();
        const url = isLinkMode
          ? `/api/link/diagrams/${diagramId}/snapshot`
          : `/api/diagrams/${diagramId}/snapshot`;
        const headers: Record<string, string> = {
          'Content-Type': 'application/octet-stream',
          'X-Snapshot-Seq': String(latestSeq),
          'X-Document-Epoch': String(documentEpochRef.current),
        };
        const response = isLinkMode
          ? await fetch(url, {
              method: 'PUT',
              headers,
              credentials: 'include',
              body: binarySnapshot,
            })
          : await authenticatedFetch(url, {
              method: 'PUT',
              headers,
              body: binarySnapshot,
            });

        if (response.status === 409) {
          logger.info('Snapshot push skipped: server has newer snapshot', { latestSeq });
          if (!cancelled) {
            lastPushedSeqRef.current = latestSeq;
            lastPushTimestampRef.current = Date.now();
            setSaveStatus('saved');
          }
          return;
        }
        if (!response.ok) {
          throw new Error(`Failed to persist snapshot: ${response.statusText}`);
        }

        if (!cancelled) {
          lastPushedSeqRef.current = latestSeq;
          lastPushTimestampRef.current = Date.now();
          setSaveStatus('saved');
          // Fire-and-forget: backup confirmed-good snapshot to IndexedDB
          LocalDocBackupStore.save(diagramId, ydoc, 'server_confirmed', latestSeq).catch(() => {});
        }
      } catch (err) {
        logger.error('Snapshot push failed', err);
        if (!cancelled) setSaveStatus('error');
      } finally {
        pushInFlightRef.current = false;
        // Re-trigger effect to pick up any seq that arrived while push was in-flight
        setPushGeneration(g => g + 1);
      }
    };

    pushSnapshot();

    return () => {
      cancelled = true;
    };
  }, [enabled, provider, electricSynced, syncMode, isReady, canWrite, encodeSnapshotBinary, authenticatedFetch, diagramId, isLinkMode, pendingCount, pushGeneration]);

  return returnValue;
}
